/**
 * components/hire/addCandidateV2Modal.tsx
 * Ingest a candidate (WI-3): identity + mandatory consent (purpose + TTL) +
 * resume text (extracted inline). Calls POST /candidates and returns the
 * created/matched CandidateDetail.
 *
 * All validation / outcome feedback is surfaced via toast only (no inline text).
 * The Phone field uses the shared MobileNumberInput (ported from SK-PLATFORM).
 */
import React, { useState, useRef } from 'react';
import { View, Text, Platform, Pressable, ActivityIndicator } from 'react-native';
import { useTheme } from '../../context/themeContext';
import { useBreakpoints } from '../../theme/responsive';
import { Modal, ActionButton, Icon } from '../ui';
import CommonInput from '../commonInput';
import { Section } from './formKit';
import { web } from '../../utils/web';
import { showToast } from '../toastComponent';
import MobileNumberInput, { COUNTRIES, type CountryEntry } from '../mobileNumberInput';
import Email_Component from '../emailInput';
import { createCandidate, extractResumeFile, extractResumeText, lookupCandidateByContact } from '../../services/hire/candidateV2';
import type { CandidateLookupHit } from '../../services/hire/candidateV2';
import type { CandidateDetail } from '../../schemas/hire/candidateV2';

const DEFAULT_COUNTRY = COUNTRIES.find((c) => c.code === 'IN') ?? COUNTRIES[0];
const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
// Names: letters (any script), spaces, hyphen and apostrophe only — NO digits
// or other special characters. `stripName` blocks them at the keystroke; the
// regex validates the whole value for the error state.
const stripName = (v: string) => v.replace(/[^\p{L} '-]/gu, '');
const NAME_RE = /^\p{L}[\p{L} '-]*$/u;
const RESUME_ACCEPT = '.pdf,.csv,.xlsx,.xls,application/pdf,text/csv,application/vnd.ms-excel,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet';

/** Read a browser File as bare base64 (strips the data: URL prefix). */
function fileToBase64(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(String(reader.result).split(',')[1] ?? '');
    reader.onerror = () => reject(reader.error);
    reader.readAsDataURL(file);
  });
}

export default function AddCandidateV2Modal({
  open, onClose, onDone,
}: { open: boolean; onClose: () => void; onDone: (c: CandidateDetail, duplicate: boolean) => void }) {
  const { theme } = useTheme();
  const { colors, fontSize, fontWeight, radii, shadows } = theme;
  const { isMobile } = useBreakpoints();
  // Two-column field rows collapse to a single column on phones so each field
  // keeps a legible width.
  const rowStyle = { flexDirection: isMobile ? ('column' as const) : ('row' as const), gap: isMobile ? 16 : 12 };
  const cellStyle = isMobile ? ({ width: '100%' } as const) : ({ flex: 1, minWidth: 0 } as const);

  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [phone, setPhone] = useState('');            // national digits only
  const [phoneCountry, setPhoneCountry] = useState<CountryEntry>(DEFAULT_COUNTRY);
  const [purpose, setPurpose] = useState('Recruitment matching');
  const [ttlDays, setTtlDays] = useState('365');
  const [resume, setResume] = useState('');
  const [busy, setBusy] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [extracting, setExtracting] = useState(false); // paste-a-resume → Extract
  // Contact already in the pool → the "add anyway?" confirmation.
  const [checking, setChecking] = useState(false);
  const [existing, setExisting] = useState<CandidateLookupHit | null>(null);
  const [pendingPhone, setPendingPhone] = useState<string | null>(null);
  const [touched, setTouched] = useState(false); // forces inline errors after a failed submit
  const [nameError, setNameError] = useState<string | null>(null); // live name-char error
  const toastGuard = useRef(false); // throttles the "not allowed" toast
  const [sourceFileId, setSourceFileId] = useState<string | null>(null);
  const [sourceFilename, setSourceFilename] = useState<string | null>(null);

  const reset = () => {
    setName(''); setEmail(''); setPhone(''); setPhoneCountry(DEFAULT_COUNTRY);
    setPurpose('Recruitment matching'); setTtlDays('365'); setResume('');
    setSourceFileId(null); setSourceFilename(null); setTouched(false); setNameError(null);
  };

  // Upload a resume file → server parses (PDF→text / CSV / Excel), AI-scrapes the
  // contact identity, and stores the original in SK-Drive.
  const onUploadResume = () => {
    if (Platform.OS !== 'web') { showToast('error', 'Upload on web', 'Resume upload is available in the web app.'); return; }
    const doc: any = (globalThis as any).document;
    const el = doc.createElement('input');
    el.type = 'file';
    el.accept = RESUME_ACCEPT;
    el.onchange = () => { const f = el.files?.[0]; if (f) void onResumeSelected(f as File); };
    el.click();
  };

  // Split an AI-extracted phone (e.g. "+91 98840 12345") into a matched country
  // dial code + national digits, so it lands correctly in the phone input.
  const applyExtractedPhone = (raw: string) => {
    const cleaned = raw.replace(/[^\d+]/g, '');
    // Longest matching dial code wins (e.g. +1 vs +1876).
    const match = cleaned.startsWith('+')
      ? [...COUNTRIES].filter((c) => cleaned.startsWith(c.dialCode)).sort((a, b) => b.dialCode.length - a.dialCode.length)[0]
      : undefined;
    if (match) {
      setPhoneCountry(match);
      setPhone(cleaned.slice(match.dialCode.length).replace(/\D/g, '').slice(0, match.digitCount));
    } else {
      // No leading '+'. Peel the two things that otherwise push the real number
      // out of the slice window and corrupt it: a national trunk '0'
      // ("0 98407 21134") and a bare dial code ("91 98407 21134"). Only peel
      // when what remains is exactly the expected length, so a genuine local
      // number starting with 0 is never mangled.
      let digits = cleaned.replace(/\D/g, '');
      const want = phoneCountry.digitCount;
      const bare = phoneCountry.dialCode.replace(/\D/g, '');
      if (digits.length > want && digits.startsWith('0') && digits.slice(1).length === want) digits = digits.slice(1);
      else if (digits.length > want && digits.startsWith(bare) && digits.slice(bare.length).length === want) digits = digits.slice(bare.length);
      setPhone(digits.slice(0, want));
    }
  };

  /**
   * Seat a scraped contact into the identity fields. Shared by the upload path
   * and the paste-then-Extract path so both fill the form identically.
   *
   * The name goes through the SAME sanitiser a keystroke goes through. Resumes
   * carry initials and honorifics ("A. Prakash", "Dr. Ravi Kumar") and the field
   * rejects periods — seating raw would fill the box with a value that then
   * blocks submit. Returns the list of fields that actually landed.
   */
  const applyContact = (r: { name: string | null; email: string | null; phone: string | null }): string[] => {
    if (r.name) setName(stripName(r.name).replace(/\s{2,}/g, ' ').trim());
    if (r.email) setEmail(r.email.trim());
    if (r.phone) applyExtractedPhone(r.phone);
    return [r.name && 'name', r.email && 'email', r.phone && 'phone'].filter(Boolean) as string[];
  };

  /**
   * Say what actually landed. Claiming "scraped the details" over three empty
   * boxes reads as a bug; naming the gap tells the recruiter to type.
   */
  const contactToast = (got: string[], note = '') => {
    if (got.length === 0) {
      showToast('success', 'Read the resume', `Couldn't pick out the contact details — enter them below.${note}`);
      return;
    }
    const missing = ['name', 'email', 'phone'].filter((f) => !got.includes(f));
    showToast(
      'success',
      'Read from resume',
      missing.length
        ? `Filled ${got.join(', ')} — add the ${missing.join(' and ')} yourself, then review.${note}`
        : `Scraped the details — review before saving.${note}`,
    );
  };

  const onResumeSelected = async (file: File) => {
    setUploading(true);
    try {
      const contentBase64 = await fileToBase64(file);
      const r = await extractResumeFile({ filename: file.name, contentBase64, contentType: file.type || 'application/octet-stream' });
      const got = applyContact(r);
      if (r.resumeText) setResume(r.resumeText);
      setSourceFileId(r.sourceFileId);
      setSourceFilename(r.sourceFilename);
      contactToast(got, r.sourceFileId ? '' : ' The original file could not be saved.');
    } catch (e) {
      const msg = (e as { response?: { data?: { error?: { message?: string } } } })?.response?.data?.error?.message;
      showToast('error', 'Upload failed', msg || 'Could not read that resume. Try a PDF, CSV or Excel.');
    } finally {
      setUploading(false);
    }
  };

  /**
   * Extract from the PASTED resume text — the paste twin of the upload path, and
   * the exact counterpart of the New Job card's Extract. The server AI-scrapes
   * name/email/phone from the text and warms the profile extraction, so "Add
   * candidate" afterwards no longer waits on the extractor. Nothing is stored in
   * SK-Drive (there is no uploaded original), so the source-file chip is left
   * exactly as it was.
   */
  const onExtractText = async () => {
    if (!resume.trim()) { showToast('error', 'Nothing to extract', 'Paste the resume text first.'); return; }
    setExtracting(true);
    try {
      const r = await extractResumeText(resume.trim());
      contactToast(applyContact(r));
    } catch (e) {
      const msg = (e as { response?: { data?: { error?: { message?: string } } } })?.response?.data?.error?.message;
      showToast('error', 'Extract failed', msg || 'Could not read that resume text. Fill the fields manually.');
    } finally {
      setExtracting(false);
    }
  };
  const close = () => { if (!busy) { reset(); onClose(); } };

  /** Creates the candidate. Reached either straight from Submit, or from the
   *  "already on file" confirmation when the recruiter chooses to add anyway. */
  const doCreate = async (fullPhone: string | null) => {
    setExisting(null);
    setBusy(true);
    try {
      const out = await createCandidate({
        name: name.trim(),
        email: email.trim(),
        phone: fullPhone,
        source: sourceFileId ? 'upload_form' : 'manual',
        resumeText: resume.trim(),
        sourceFileId,
        sourceFilename,
        consent: { purpose: purpose.trim(), ttlDays: Number(ttlDays) || 365, source: 'upload_form' },
      });
      const isDup = out.result === 'duplicate';
      if (isDup) {
        showToast('success', 'Added — possible duplicate', `${out.candidate.name} was flagged for review. Opening the profile.`);
      } else {
        showToast('success', 'Candidate added', `${out.candidate.name} was ingested.`);
      }
      reset();
      onDone(out.candidate, isDup);
    } catch (e) {
      const msg = (e as { response?: { data?: { error?: { message?: string } } } })?.response?.data?.error?.message;
      showToast('error', 'Could not add candidate', msg || 'Could not add the candidate. Please try again.');
    } finally {
      setBusy(false);
    }
  };

  const onSubmit = async () => {
    setTouched(true); // reveal inline field errors
    if (name.trim() && !NAME_RE.test(name.trim())) {
      showToast('error', 'Invalid name', 'Name can contain letters, spaces, hyphens and apostrophes only.');
      return;
    }
    // All validation feedback is toast-only.
    if (!name.trim() || !email.trim()) {
      showToast('error', 'Missing required fields', 'Name and email are required.');
      return;
    }
    if (!EMAIL_RE.test(email.trim())) {
      showToast('error', 'Invalid email', 'Enter a valid email address.');
      return;
    }
    if (!purpose.trim()) {
      showToast('error', 'Consent purpose required', 'A consent purpose is required (DPDP).');
      return;
    }
    // Phone is optional — but if started, it must be complete for the country.
    if (phone.length > 0 && phone.length !== phoneCountry.digitCount) {
      showToast('error', 'Incomplete phone number', `${phoneCountry.name} numbers need ${phoneCountry.digitCount} digits, or leave it blank.`);
      return;
    }
    const fullPhone = phone.length > 0 ? `${phoneCountry.dialCode}${phone}` : null;

    // Already on file? Ask first. A repeat no longer blocks — the create
    // succeeds and flags — so the decision is the recruiter's, not a 409's.
    setChecking(true);
    let hit: CandidateLookupHit | null = null;
    try {
      hit = await lookupCandidateByContact(email.trim(), fullPhone);
    } catch { /* non-fatal: the create still flags duplicates server-side */ }
    setChecking(false);
    if (hit) { setPendingPhone(fullPhone); setExisting(hit); return; }

    await doCreate(fullPhone);
  };

  // Shared SK-PLATFORM CommonInput (uppercase label + focus/error states).
  const ci = (
    label: string,
    value: string,
    set: (v: string) => void,
    o?: { placeholder?: string; required?: boolean; multiline?: boolean; minHeight?: number; numeric?: boolean; isValid?: (v: string) => boolean; error?: string },
  ) => {
    // Show the error message (and red border) only when actually invalid — a
    // constant `error` would otherwise force a permanent error state.
    const bad = o?.isValid ? !o.isValid(value) : false;
    return (
      <CommonInput
        label={label}
        required={o?.required}
        value={value}
        onChangeText={(v) => set(o?.numeric ? v.replace(/[^\d]/g, '') : v)}
        placeholder={o?.placeholder}
        isValid={o?.isValid}
        error={bad ? o?.error : undefined}
        touched={touched}
        mono={o?.numeric}
        inputProps={o?.multiline ? { multiline: true, style: { minHeight: o.minHeight ?? 100, textAlignVertical: 'top', paddingTop: 2, lineHeight: 21 } } : (o?.numeric ? { keyboardType: 'numeric' } : undefined)}
        containerStyle={{ marginBottom: 0 }}
      />
    );
  };

  // Throttled "not allowed" toast — shared by the name + phone char guards so a
  // burst of blocked keystrokes shows the toast at most once every 2s.
  const warnBadChar = (msg: string) => {
    if (toastGuard.current) return;
    toastGuard.current = true;
    showToast('error', 'Not allowed', msg);
    setTimeout(() => { toastGuard.current = false; }, 2000);
  };

  // Name keystroke handler — blocks digits/specials, but SURFACES why: an inline
  // error + a throttled toast so the user gets feedback.
  const onNameChange = (v: string) => {
    if (/[^\p{L} '-]/u.test(v)) {
      setNameError('Numbers and special characters are not allowed');
      warnBadChar('Numbers and special characters are not allowed in the name.');
    } else {
      setNameError(null);
    }
    setName(stripName(v));
  };

  return (
    <Modal
      open={open}
      onClose={close}
      title="Add candidate"
      size="wide"
      overlay={existing ? (
        <OverlayScrim>
          <OverlayCard accent={colors.warn}>
            <View style={{ width: 52, height: 52, borderRadius: 26, alignItems: 'center', justifyContent: 'center', backgroundColor: colors.warnSoft, alignSelf: 'center' }}>
              <Icon name="dup" size={24} color={colors.warn} />
            </View>
            <Text style={{ fontSize: fontSize.lg, fontWeight: fontWeight.bold, color: colors.ink, textAlign: 'center' }}>
              This person is already on file
            </Text>
            <Text style={{ fontSize: fontSize.sm, color: colors.ink2, textAlign: 'center', lineHeight: 20 }}>
              {existing.name} ({existing.code}) already holds this contact
              {existing.status !== 'active' ? `, and their status is “${existing.status}”` : ''}.
              {' '}Add anyway and you get a second record, flagged as a possible duplicate on the profile so you can
              merge or dismiss it later. Nothing on the existing record is changed.
            </Text>
            <View style={{ flexDirection: 'row', gap: 10, marginTop: 6 }}>
              <View style={{ flex: 1 }}>
                <ActionButton label="Go back" variant="secondary" onPress={() => setExisting(null)} disabled={busy} />
              </View>
              <View style={{ flex: 1 }}>
                <ActionButton label="Add anyway" variant="primary" icon="check" loading={busy} onPress={() => doCreate(pendingPhone)} />
              </View>
            </View>
          </OverlayCard>
        </OverlayScrim>
      ) : undefined}
      footer={
        <View style={{ flexDirection: 'row', gap: 8 }}>
          <ActionButton label="Cancel" variant="secondary" onPress={close} />
          <ActionButton label="Add candidate" variant="primary" onPress={onSubmit} loading={busy || checking} />
        </View>
      }
    >
      <View style={{ gap: 20 }}>
        {/* Intro strip + resume upload. Leads the card, so it carries the
            entrance: a soft gradient panel that rises in before the sections. */}
        <View
          style={[
            { flexDirection: isMobile ? 'column' : 'row', alignItems: isMobile ? 'stretch' : 'center', justifyContent: 'space-between', gap: isMobile ? 12 : 10, flexWrap: 'wrap', paddingVertical: 13, paddingHorizontal: 14, borderRadius: radii.lg, borderWidth: 1, borderColor: colors.line, backgroundColor: colors.paper2, marginTop: -2 },
            web({
              animation: 'sk-rise 300ms cubic-bezier(.2,.8,.2,1) both',
              backgroundImage: `linear-gradient(180deg, ${colors.card}, ${colors.paper2})`,
              boxShadow: '0 1px 2px rgba(0,0,0,0.04)',
            } as any),
          ]}
        >
          <View style={{ flexDirection: 'row', alignItems: isMobile ? 'flex-start' : 'center', gap: 9, flex: isMobile ? undefined : 1, minWidth: 0 }}>
            <View
              style={[
                { width: 26, height: 26, borderRadius: 8, alignItems: 'center', justifyContent: 'center', backgroundColor: colors.card, borderWidth: 1, borderColor: colors.line },
                web({ boxShadow: `0 0 0 3px ${colors.accentSoft}` } as any),
              ]}
            >
              <Icon name="sparkles" size={13} color={colors.ink2} />
            </View>
            <Text style={{ fontSize: fontSize.sm, color: colors.ink3, flex: 1, minWidth: 0, lineHeight: 19 }}>
              One-time AI extraction · consent-governed (DPDP) · byte-identical re-uploads cost nothing
            </Text>
          </View>
          <ActionButton label="Upload resume" variant="secondary" size="sm" icon="upload" onPress={onUploadResume} loading={uploading} />
        </View>
        {sourceFilename && (
          <View style={[
            { flexDirection: 'row', alignItems: 'center', gap: 8, alignSelf: 'flex-start', paddingHorizontal: 10, height: 30, borderRadius: radii.md, borderWidth: 1, borderColor: colors.line, backgroundColor: colors.paper2, marginTop: -8 },
            web({ animation: 'sk-pop 240ms cubic-bezier(.2,.9,.2,1) both' } as any),
          ]}>
            <Icon name="import" size={13} color={colors.ink3} />
            <Text style={{ fontSize: fontSize.xs, color: colors.ink2, fontWeight: '600' }} numberOfLines={1}>{sourceFilename}</Text>
            {sourceFileId ? <Icon name="check-circle" size={13} color={colors.success} /> : <Text style={{ fontSize: 10, color: colors.ink4 }}>not stored</Text>}
            <Pressable onPress={() => { setSourceFileId(null); setSourceFilename(null); }} style={web({ cursor: 'pointer' })}>
              <Icon name="x" size={13} color={colors.ink4} />
            </Pressable>
          </View>
        )}

        {/* ── Resume ── */}
        <Section icon="list" title="Resume" hint="skills · experience · titles" first delay={50}>
          {/* Resume leads the card, mirroring the New Job card where Source sits
              first and fills the structured fields under it: the recruiter pastes,
              presses Extract, and the identity below fills itself. */}
          <View style={{ flexDirection: 'row', alignItems: 'center', gap: 10 }}>
            <Text style={{ fontSize: fontSize.xs, color: colors.ink4, flex: 1, minWidth: 0 }} numberOfLines={1}>
              Paste the resume, then Extract to fill the identity below.
            </Text>
            <ActionButton label="Extract" variant="secondary" size="sm" icon="wand" onPress={onExtractText} loading={extracting} />
          </View>
          <View style={{ position: 'relative' }}>
            {/* Resume text is stored VERBATIM — no character filtering. A resume is
                full of ₹, %, +, /, •, @, digits and punctuation, and this text is
                what the profile extractor reads; stripping anything from it would
                degrade the extraction and the match that follows. */}
            {ci('Resume text', resume, setResume, { multiline: true, minHeight: 132, placeholder: 'Paste resume text — any characters, symbols or numbers. Extract fills the name, email and phone below.' })}
            {/* Live character count — only once there is something to count, so
                the empty box stays clean. */}
            {resume.length > 0 && !extracting && (
              <View
                style={[
                  { position: 'absolute', right: 10, bottom: 8, paddingHorizontal: 7, height: 18, borderRadius: 9, alignItems: 'center', justifyContent: 'center', backgroundColor: colors.paper2, borderWidth: 1, borderColor: colors.line },
                  web({ animation: 'sk-pop 220ms cubic-bezier(.2,.9,.2,1) both' } as any),
                ]}
                pointerEvents="none"
              >
                <Text style={{ fontSize: 10, color: colors.ink4, fontFamily: theme.font.mono }}>{resume.length.toLocaleString()}</Text>
              </View>
            )}
            {extracting && (
              <View
                style={[
                  { position: 'absolute', top: 0, left: 0, right: 0, bottom: 0, alignItems: 'center', justifyContent: 'center', gap: 8, borderRadius: radii.md, backgroundColor: colors.paper2 },
                  web({ opacity: 0.94, backdropFilter: 'blur(2px)', WebkitBackdropFilter: 'blur(2px)' } as any),
                ]}
              >
                <ActivityIndicator size="small" color={colors.accent} />
                <Text
                  style={[
                    { fontSize: fontSize.sm, color: colors.ink3, fontWeight: fontWeight.medium },
                    web({ animation: 'sk-breathe 1400ms ease-in-out infinite' } as any),
                  ]}
                >
                  Reading the resume…
                </Text>
              </View>
            )}
          </View>
        </Section>

        {/* ── Identity ── */}
        <Section icon="user" title="Identity" delay={100}>
          <View style={[rowStyle, !isMobile && { alignItems: 'flex-start' }]}>
            <View style={cellStyle}>
              <CommonInput
                label="Name"
                required
                value={name}
                onChangeText={onNameChange}
                placeholder="e.g. Gokulakrishnan"
                isValid={(v) => v.trim().length > 0 && NAME_RE.test(v.trim())}
                error={nameError ?? (touched && !name.trim() ? 'Name is required' : undefined)}
                touched={touched}
                // No height override: Email_Component now carries CommonInput's own
                // box metrics, so the two fields line up on their own. Forcing a
                // height here was compensating for that mismatch and is what made
                // the two borders read as different heights.
                inputProps={{
                  onBlur: () => { if (!name.trim()) showToast('error', 'Name is required', 'Enter the candidate name.'); },
                }}
                containerStyle={{ marginBottom: 0 }}
              />
            </View>
            <View style={cellStyle}>
              <Email_Component
                required
                value={email}
                onValueChange={setEmail}
                placeholder="e.g. gokul@gmail.com"
                onBlur={() => {
                  if (!email.trim()) showToast('error', 'Email is required', 'Enter an email address.');
                  else if (!EMAIL_RE.test(email.trim())) showToast('error', 'Invalid email', 'Enter a valid email address.');
                }}
              />
            </View>
          </View>
          <MobileNumberInput
            label="PHONE"
            value={phone}
            selectedCountry={phoneCountry}
            onCountryChange={setPhoneCountry}
            onChangeText={setPhone}
            onInvalidChar={() => warnBadChar('Only numbers are allowed in the phone field.')}
            placeholder="e.g. 9876543210 (optional)"
          />
        </Section>

        {/* ── Consent (DPDP) ── */}
        <Section icon="shield" title="Consent" hint="DPDP — required" delay={150}>
          <View style={rowStyle}>
            <View style={cellStyle}>{ci('Consent purpose', purpose, setPurpose, { required: true, placeholder: 'Recruitment matching', isValid: (v) => v.trim().length > 0, error: 'A consent purpose is required' })}</View>
            <View style={cellStyle}>{ci('Consent TTL (days)', ttlDays, setTtlDays, { numeric: true, placeholder: '365' })}</View>
          </View>
        </Section>

      </View>
    </Modal>
  );
}

/** Full-card scrim for the in-modal prompt (a second RN Modal would render
 *  beneath this dialog and never be seen). */
function OverlayScrim({ children }: { children: React.ReactNode }) {
  return (
    <View
      style={[
        { position: 'absolute', top: 0, left: 0, right: 0, bottom: 0, alignItems: 'center', justifyContent: 'center', padding: 20, backgroundColor: 'rgba(8,10,14,0.55)' },
        web({ animation: 'sk-rise 160ms ease both', backdropFilter: 'blur(2px)', WebkitBackdropFilter: 'blur(2px)' } as any),
      ]}
    >
      {children}
    </View>
  );
}

/** The centered dialog card used inside an OverlayScrim. */
function OverlayCard({ accent, children }: { accent: string; children: React.ReactNode }) {
  const { theme } = useTheme();
  const { colors, radii, shadows } = theme;
  return (
    <View
      style={[
        { width: '100%', maxWidth: 400, backgroundColor: colors.card, borderRadius: radii.lg, borderWidth: 1, borderColor: colors.line, overflow: 'hidden' },
        shadows.lg as object,
      ]}
    >
      <View style={{ height: 3, backgroundColor: accent }} />
      <View style={{ padding: 20, gap: 12 }}>{children}</View>
    </View>
  );
}
